import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { Router } from '@angular/router';
import { ConceptInvoicingTypeEnums } from 'src/app/enum/concept-invoicing-enums';
import { ActionColor, CheckBoxAction, TableAction } from 'src/app/interfaces/custom-table/custom-table-action.interface';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { InvoicingModelDetailCaptureTableModel } from 'src/app/models/tables/management/invoicing-model-detail-capture-table.model';
import { ConfirmDialogService } from 'src/app/services/confirm-dialog/confirm-dialog.service';
import { InvoicingModelDetailService } from 'src/app/services/invoicing/invoicing-model-detail.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';
import { InvoicingModelDetailModel } from './../../../../models/invoicing/invoicing-model-detail.model';
import { InvoicingModelModel } from '../../../../models/invoicing/invoicing-model.model';
import { CustomModalComponent } from 'src/app/components/custom-modal/custom-modal.component';
import { ImportInvoicingModelDetailComponent } from '../import-invoicing-model-detail/import-invoicing-model-detail.component';
import { CopyInvoicingModelComponent } from '../copy-invoicing-model/copy-invoicing-model.component';
import { InvoicingModelDetailCopy } from 'src/app/models/invoicing/invoicing-model-detail-copy.model';

@Component({
  selector: 'app-invoicing-model-detail',
  templateUrl: './invoicing-model-detail.component.html',
  styleUrls: ['./invoicing-model-detail.component.scss'],
  standalone: false
})
export class InvoicingModelDetailComponent implements OnInit, OnChanges {

  @ViewChild('invoicingModelDetailImportModal') invoicingModelDetailImportModalComponent?: CustomModalComponent | null = null;
  @ViewChild('copyInvoicingModelDetailImportFromModal') copyInvoicingModelDetailImportFromModalComponent?: CustomModalComponent | null = null;

  @Input() invoicingModelModel: InvoicingModelModel = new InvoicingModelModel();
  @Input() disabledDetail: boolean = false;
  @Input() updateListDetail: boolean = false;
  @Output() emitQuantityDetails = new EventEmitter<number>();
  ascendant: boolean = true;
  checkboxActions: CheckBoxAction[] = [
    {
      label: 'Eliminar',
      icon: 'pi pi-trash',
      color: ActionColor.DANGER,
      action: 'bulk-delete'
    },
  ];
  clearSelectionTrigger: number = 0;
  componentInvoicingModelDetailImportModal = ImportInvoicingModelDetailComponent;
  componentCopyInvoicingModelDetailImportFromModal = CopyInvoicingModelComponent;
  columnSort: string | null = null;
  detailData: PagedResponseModel<InvoicingModelDetailCaptureTableModel> = new PagedResponseModel<InvoicingModelDetailCaptureTableModel>();
  detailHeader: ColumnResponseModel[] = [];
  invoicingModelDetailModel: InvoicingModelDetailModel = new InvoicingModelDetailModel();
  isFormView: boolean = false;
  inputsForInvoicingModelDetailImportModal$ = new BehaviorSubject<any>({
    invoicingModelDetailModel: new InvoicingModelDetailModel,
  });
  inputsForCopyInvoicingModelDetailFromModal$ = new BehaviorSubject<any>({
    invoicingModelDetailModel: new InvoicingModelDetailModel(),
  });
  limit: number = 10;
  page: number = 1;
  tableActions: TableAction[] = [];
  conceptInvoicingType: number = ConceptInvoicingTypeEnums.venta;

  constructor(private confirmDialogService: ConfirmDialogService,
    private invoicingModelDetailService: InvoicingModelDetailService, private toastHandlerService: ToastHandlerService,
    private router: Router) {
  }

  async ngOnInit(): Promise<void> {
    this.getUrl();
    await this.getColumns();
    this.tableActions = this.getDefaultActions();
  }

  getUrl() {
    const url = this.router.url as string;
    if (url && url !== '/') {
      const parts = url.split('/');
      const mainUrl = parts[1];
      this.conceptInvoicingType = mainUrl === 'purchase-model' ? ConceptInvoicingTypeEnums.compra : ConceptInvoicingTypeEnums.venta;
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    const updateListDetail = changes["updateListDetail"];
    if (updateListDetail && !updateListDetail.firstChange) {
      this.getInvoicingModelDetailsByInvoicingModel();
    }
    const invoicingModelModel = changes["invoicingModelModel"];
    if (invoicingModelModel && !invoicingModelModel.firstChange) {
      this.getFormView(false);
      this.getInvoicingModelDetailsByInvoicingModel();
      this.invoicingModelDetailModel = new InvoicingModelDetailModel();
      this.tableActions = this.disabledDetail ? this.getDefaultActions() : this.getDefaultActions();
    }
  }

  async createNewDetail() {
    this.isFormView = true;
    this.invoicingModelDetailModel = new InvoicingModelDetailModel();
  }

  async createInvoicingModelDetails(invoicingModelDetails: InvoicingModelDetailCopy[]) {
    try {
      const message = this.conceptInvoicingType === ConceptInvoicingTypeEnums.compra
        ? 'El detalle del modelo de compra fue creado exitosamente'
        : 'El detalle del modelo de facturación fue creado exitosamente';
      await firstValueFrom(this.invoicingModelDetailService.createInvoicingModelDetails(invoicingModelDetails));
      this.toastHandlerService.showToast('success', message);
      await this.getInvoicingModelDetailsByInvoicingModel();
    } catch (error) {
      const message = this.conceptInvoicingType === ConceptInvoicingTypeEnums.compra
        ? 'Error al crear los detalles del modelo de compra'
        : 'Error al crear los detalles del modelo de facturación';
      console.error(message, error);
    }
  }

  async deleteInvoicingModelDetail(selectedIds: number[]) {
    if (selectedIds.length === 0) return;

    const isCompra = this.conceptInvoicingType === ConceptInvoicingTypeEnums.compra;
    const confirmMessage = isCompra ? "¿Realmente desea eliminar el detalle del modelo de compra?"
      : "¿Realmente desea eliminar el detalle del modelo de facturación?";

    if (await this.confirmDialogService.confirm(confirmMessage)) {
      try {
        await firstValueFrom(this.invoicingModelDetailService.deleteSomeDetails(selectedIds));
        this.toastHandlerService.showToast(
          'success',
          'El detalle fue eliminado exitosamente.',
          undefined,
          undefined,
          5000
        );
        this.clearSelectionTrigger++;
        this.getUpdateListDetailByDelete();
      } catch (error) {
        this.getUpdateListDetailByDelete();
        console.error(`Error al eliminar los detalles del modelo de ${isCompra ? 'compra' : 'facturación'}.`, error);
      }
    }
  }

  async getColumns() {
    try {
      if (this.conceptInvoicingType === ConceptInvoicingTypeEnums.compra) {
        this.detailHeader = await firstValueFrom(this.invoicingModelDetailService.getColumnsForCaptureTablePurchaseConceptAsync());
      } else {
        this.detailHeader = await firstValueFrom(this.invoicingModelDetailService.getColumnsForCaptureTable());
      }
    } catch (error) {
      console.error('Error al obtener las columnas para la tabla del detalle.', error);
    }
  }

  getDefaultActions(): TableAction[] {
    return [
      {
        label: 'Editar',
        icon: 'pi pi-pencil',
        action: 'edit'
      },
    ];
  }

  getDefaultActionsDisabled(): TableAction[] {
    return [];
  }

  async getInvoicingModelDetailsByInvoicingModel() {
    try {
      if (this.conceptInvoicingType === ConceptInvoicingTypeEnums.compra) {
        this.detailData = await firstValueFrom(
          this.invoicingModelDetailService.getPurchaseModelDetailsForCaptureTableAsync(
            this.page, this.limit, this.columnSort, this.ascendant, this.invoicingModelModel.invoicingModelId
          )
        );
      } else {
        this.detailData = await firstValueFrom(
          this.invoicingModelDetailService.getInvoicingModelDetailsForCaptureTable(
            this.page, this.limit, this.columnSort, this.ascendant, this.invoicingModelModel.invoicingModelId
          )
        );
      }
      this.emitQuantityDetails.emit(this.detailData.totalItems);
    } catch (error) {
      console.error('Error al obtener los detalles del modelo de facturación.', error);
    }
  }

  async getInvoicingModelDetailById(invoicingModelDetailId: number) {
    try {
      this.invoicingModelDetailModel = await firstValueFrom(this.invoicingModelDetailService.getInvoicingModelDetailById(invoicingModelDetailId));
    } catch (error) {
      console.error('Error al obtener el detalle del modelo de facturación por ID.', error);
    }
  }

  getFormView(isFormView: boolean) {
    this.isFormView = isFormView;
  }

  getUpdateListDetailByDelete() {
    this.invoicingModelDetailModel = new InvoicingModelDetailModel();
    this.clearSelectionTrigger++;
    this.getInvoicingModelDetailsByInvoicingModel();
  }

  async onAction(event: { action: string, item?: InvoicingModelDetailCaptureTableModel, selectedItems?: number[] }) {
    if (event.action === 'edit') {
      this.getFormView(true);
      await this.getInvoicingModelDetailById(event.item!.id);
    }
    if (event.action === 'bulk-delete' && event.selectedItems) {
      this.deleteInvoicingModelDetail(event.selectedItems);
    }
  }

  async handleInvoicingModelDetailSelection(selectedItem: any) {
    const invoicingModelDetails = selectedItem.map((element: any) => ({
      invoicingModelId: this.invoicingModelModel.invoicingModelId,
      invoicingModelDetailId: element.id
    }));
    await this.createInvoicingModelDetails(invoicingModelDetails);
  }

  openCopyFromModal() {
    if (this.invoicingModelModel.invoicingModelId === 0) {
      this.toastHandlerService.showToast('warning', 'El modelo de facturación aún no se ha guardado.');
      return;
    }
    this.inputsForCopyInvoicingModelDetailFromModal$.next({
      invoicingModelId: this.invoicingModelModel.invoicingModelId,
      conceptInvoicingType: this.conceptInvoicingType
    });
    this.copyInvoicingModelDetailImportFromModalComponent?.show();
  }

  openInvoicingModelDetailImportModal() {
    if (this.invoicingModelModel.invoicingModelId === 0) {
      this.toastHandlerService.showToast('warning', `El modelo aún no se ha guardado.`);
      return;
    }
    if (this.detailData.totalItems > 0) {
      this.toastHandlerService.showToast('warning', `No se puede importar, el modelo ya tiene detalle.`);
      return;
    }
    this.inputsForInvoicingModelDetailImportModal$.next({
      invoicingModelId: this.invoicingModelModel.invoicingModelId,
      conceptInvoicingType: this.conceptInvoicingType
    });
    this.invoicingModelDetailImportModalComponent?.show();
  }

  async onPageChange(event: { page: number; pageSize: number, sortField: string | undefined, sortOrder: number }) {
    this.page = event.page;
    this.limit = event.pageSize;
    this.columnSort = event.sortField || null;
    this.ascendant = event.sortOrder === 1;
    this.getInvoicingModelDetailsByInvoicingModel();
  }

  async reload(reload: any) {
    if (reload) {
      await this.getInvoicingModelDetailsByInvoicingModel();
    }
  }
}
